<script setup lang="ts">
import { useErrorStore } from '@/store/error'
import { useLoadingStore } from '@/store/loading'
import { useMaintenanceStore } from '@/store/maintenance'
import { IMaintenance } from '@/interfaces/entities/maintenance'
import { formatDateWithTime } from '@/utils/date/date-time'
import { DataTable } from 'simple-datatables'
import { computed, nextTick, onMounted, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRouter } from 'vue-router'

const { t } = useI18n()
const router = useRouter()

const maintenanceStore = useMaintenanceStore()
const isLoading = useLoadingStore()
const error = useErrorStore()
const page = ref(1)
const items = ref(10)
const itemsPerPageOptions = ref([10, 25, 50, 100])
const sort = ref('id')
const sort_direction = ref('desc')
let dataTableInstance: DataTable | null = null

const columns = ref([
  { title: t('maintenance_listing.body.table.columns.id'), key: 'id', sortable: true },
  { title: t('maintenance_listing.body.table.columns.status'), key: 'status', sortable: true },
  { title: t('maintenance_listing.body.table.columns.priority'), key: 'priority', sortable: true },
  { title: t('maintenance_listing.body.table.columns.description'), key: 'description', sortable: true },
  { title: t('maintenance_listing.body.table.columns.maintenance_type'), key: 'maintenance_type', sortable: true },
  { title: t('maintenance_listing.body.table.columns.sensor'), key: 'sensor', sortable: true },
  { title: t('maintenance_listing.body.table.columns.origin'), key: 'origin', sortable: true },
  { title: t('maintenance_listing.body.table.columns.user'), key: 'user', sortable: true },
  { title: t('maintenance_listing.body.table.columns.started_at'), key: 'started_at', sortable: true },
  { title: t('maintenance_listing.body.table.columns.finished_at'), key: 'finished_at', sortable: true },
  { title: t('maintenance_listing.body.table.columns.actions_taken'), key: 'actions_taken', sortable: true },
  { title: t('maintenance_listing.body.table.columns.requires_shutdown'), key: 'requires_shutdown', sortable: true },
  { title: t('maintenance_listing.body.table.columns.scheduled_for'), key: 'scheduled_for', sortable: true },
])

const filterStates = computed(() => ({
  actionsFilter: maintenanceStore.currentActionsFilter,
  descriptionFilter: maintenanceStore.currentDescriptionFilter,
  sensorFilter: maintenanceStore.currentSensorFilter,
  statusFilter: maintenanceStore.currentStatusFilter,
  origimFilter: maintenanceStore.currentOrigimFilter,
  priorityFilter: maintenanceStore.currentPriorityFilter,
  startDateFilter: maintenanceStore.currentStartDateFilter,
  endDateFilter: maintenanceStore.currentEndDateFilter,
  maintenanceTypeFilter: maintenanceStore.currentMaintenanceTypeFilter,
}))

onMounted(async () => {
  loadPage(1)
  await nextTick()
  initDataTable()
})

watch(
  filterStates,
  () => {
    sort.value = 'id'
    sort_direction.value = 'asc'
    page.value = 1
  },
  { deep: true },
)

function handleSort(field: string) {
  if (sort.value === field) {
    sort_direction.value = sort_direction.value === 'asc' ? 'desc' : 'asc'
  } else {
    sort.value = field
    sort_direction.value = 'asc'
  }
  loadPage(1)
}

function initDataTable() {
  const dataTableElement = document.querySelector('#dataTableTwo')
  if (dataTableElement && maintenanceStore.maintenances.length > 0) {
    if (dataTableInstance) {
      dataTableInstance.destroy()
      dataTableInstance = null
    }
    dataTableInstance = new DataTable('#dataTableTwo', {
      perPageSelect: itemsPerPageOptions.value,
      searchable: false,
      perPage: items.value,
      paging: false,
      columns: columns.value.map((col) => ({
        select: columns.value.findIndex((c) => c.key === col.key),
        sortable: false,
      })),
    })

    dataTableInstance.on('datatable.perpage', (perPage: number) => {
      items.value = perPage
      loadPage(1)
    })
  }
}

async function loadPage(pageNumber: number) {
  page.value = pageNumber
  await maintenanceStore.getMaintenancesPages(page.value, items.value, sort.value, sort_direction.value)
  await nextTick()
  initDataTable()
}

function handleItemsPerPageChange(event: Event) {
  const target = event.target as HTMLSelectElement
  items.value = parseInt(target.value)
  loadPage(1)
}

function openMaintenanceDetails(dataItem: IMaintenance) {
  if (!dataItem?.id) return
  router.push({
    name: 'maintenance-detail',
    params: { maintenanceId: String(dataItem.id) },
  })
}

function getVisiblePages(currentPage: number, totalPages: number, maxVisible: number = 7) {
  const pages: (number | string)[] = []

  if (totalPages <= maxVisible) {
    for (let i = 1; i <= totalPages; i++) {
      pages.push(i)
    }
  } else {
    pages.push(1)

    if (currentPage <= 4) {
      for (let i = 2; i <= 5; i++) {
        pages.push(i)
      }
      pages.push('...')
      pages.push(totalPages)
    } else if (currentPage >= totalPages - 3) {
      pages.push('...')
      for (let i = totalPages - 4; i <= totalPages; i++) {
        pages.push(i)
      }
    } else {
      pages.push('...')
      for (let i = currentPage - 1; i <= currentPage + 1; i++) {
        pages.push(i)
      }
      pages.push('...')
      pages.push(totalPages)
    }
  }

  return pages
}
</script>

<template>
  <div class="mx-8 bg-white py-2 shadow dark:bg-gray-900">
    <div v-if="isLoading.loading" class="flex items-center justify-center">
      <svg class="dark:text-blue-400 h-5 w-5 animate-spin text-blue-600 lg:h-7 lg:w-7" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
        <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
        <path
          class="opacity-75"
          fill="currentColor"
          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
      </svg>
    </div>

    <div v-else-if="error.getErrors.length > 0" class="p-4 text-red-500">
      {{ t('maintenance_listing.errors.error_data') }}
    </div>

    <div v-else-if="maintenanceStore.maintenances.length <= 0" class="p-4 text-red-500">
      {{ t('maintenance_listing.errors.no_data_found') }}
    </div>

    <div v-else class="overflow-x-auto">
      <div class="mb-4 flex items-center justify-between px-4">
        <div class="text-sm text-gray-500">Total de registros: {{ maintenanceStore.getPagination?.count ?? 0 }}</div>
        <div class="flex items-center space-x-2">
          <label for="itemsPerPage" class="text-sm font-medium text-gray-700 dark:text-gray-200"> Itens por página: </label>
          <div class="relative">
            <select
              id="itemsPerPage"
              v-model="items"
              @change="handleItemsPerPageChange"
              class="w-16 bg-white px-3 py-1 text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-800 dark:text-white">
              <option v-for="option in itemsPerPageOptions" :key="option" :value="option">
                {{ option.toString().padStart(2, '0') }}
              </option>
            </select>
            <div class="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-gray-700 dark:text-gray-300">
              <svg class="h-2 w-5.5 text-blue-600" fill="currentColor" viewBox="0 0 10 5">
                <path d="M5 5L10 0H0l5 5z" />
              </svg>
            </div>
          </div>
        </div>
      </div>

      <table class="min-w-full table-auto text-left text-sm text-gray-700 dark:text-gray-200" id="dataTableTwo">
        <thead>
          <tr class="bg-gray-100 text-xs font-semibold uppercase text-gray-700 dark:bg-gray-800 dark:text-gray-200">
            <template v-for="(column, index) in columns" :key="index">
              <th
                scope="col"
                class="whitespace-nowrap px-4 py-3"
                :class="{ 'cursor-pointer hover:bg-gray-200 dark:hover:bg-gray-700': column.sortable }"
                @click="column.sortable ? handleSort(column.key) : null">
                <div class="flex items-center justify-between">
                  <span>{{ column.title }}</span>
                  <div v-if="column.sortable" class="ml-2 flex flex-col items-center justify-center">
                    <svg v-if="sort === column.key && sort_direction === 'asc'" class="h-2 w-3 text-blue-600" fill="currentColor" viewBox="0 0 10 5">
                      <path d="M5 0L0 5h10L5 0z" />
                    </svg>
                    <svg v-if="sort === column.key && sort_direction === 'desc'" class="h-2 w-3 text-blue-600" fill="currentColor" viewBox="0 0 10 5">
                      <path d="M5 5L10 0H0l5 5z" />
                    </svg>
                    <div v-if="sort !== column.key" class="h-4 w-3"></div>
                  </div>
                </div>
              </th>
            </template>
          </tr>
        </thead>
        <tbody>
          <template v-for="(dataItem, index) in maintenanceStore.maintenances" :key="index">
            <tr
              :class="[
                index % 2 === 0 ? 'bg-white dark:bg-gray-800' : 'dark:bg-gray-850 bg-gray-50',
                'cursor-pointer border-b border-gray-200 transition-colors hover:bg-slate-100 dark:border-gray-700 dark:hover:bg-gray-700',
              ]"
              @click="openMaintenanceDetails(dataItem)">
              <td class="whitespace-nowrap px-4 py-3 font-medium">{{ dataItem.id ?? 'N/A' }}</td>
              <td class="whitespace-nowrap px-4 py-3">{{ t(`maintenance_listing.header.status.${dataItem.status}`) ?? 'N/A' }}</td>
              <td class="whitespace-nowrap px-4 py-3">
                <span class="inline-block px-2 py-1 text-xs font-semibold" :class="{
                  'bg-green-100 text-green-800': dataItem.priority === 'low',
                  'bg-yellow-100 text-yellow-800': dataItem.priority === 'medium',
                  'bg-red-100 text-red-800': dataItem.priority === 'high'
                }">
                  {{ t(`maintenance_listing.header.priority.${dataItem.priority}`) ?? 'N/A' }}
                </span>
              </td>
              <td class="whitespace-nowrap px-4 py-3">{{ dataItem.description?.slice(0, 66) ?? 'N/A' }}</td>
              <td class="whitespace-nowrap px-4 py-3">{{ dataItem.maintenance_type?.name ?? 'N/A' }}</td>
              <td class="whitespace-nowrap px-4 py-3">{{ dataItem.sensor?.name ?? 'N/A' }}</td>
              <td class="whitespace-nowrap px-4 py-3">{{ t(`maintenance_listing.header.origin.${dataItem.origin}`) ?? 'N/A' }}</td>
              <td class="whitespace-nowrap px-4 py-3">{{ dataItem.user?.name ?? 'N/A' }}</td>
              <td class="whitespace-nowrap px-4 py-3">{{ formatDateWithTime(dataItem.started_at) ?? 'N/A' }}</td>
              <td class="whitespace-nowrap px-4 py-3">{{ formatDateWithTime(dataItem.finished_at) ?? 'N/A' }}</td>
              <td class="whitespace-nowrap px-4 py-3">{{ dataItem.actions_taken ?? 'N/A' }}</td>
              <td class="whitespace-nowrap px-4 py-3">{{ t(`maintenance_listing.header.requires_shutdown.${dataItem.requires_shutdown}`) ?? 'N/A' }}</td>
              <td class="whitespace-nowrap px-4 py-3">{{ formatDateWithTime(dataItem.scheduled_for) ?? 'N/A' }}</td>
            </tr>
          </template>
        </tbody>
      </table>

      <div v-if="maintenanceStore.getPagination" class="mt-4 flex flex-col items-center justify-between space-y-4 px-4 sm:flex-row sm:space-y-0">
        <div class="text-sm text-gray-500">
          Mostrando {{ (page - 1) * items + 1 }} a {{ Math.min(page * items, maintenanceStore.getPagination.count) }} de
          {{ maintenanceStore.getPagination.count }} registros
        </div>

        <div class="flex items-center space-x-2">
          <button
            :disabled="!maintenanceStore.getPagination.prev"
            @click="loadPage(maintenanceStore.getPagination.prev || 1)"
            class="pagination-button"
            :class="{
              'cursor-not-allowed bg-gray-100 text-gray-400': !maintenanceStore.getPagination.prev,
              'bg-gray-100 text-gray-700 hover:bg-gray-200': maintenanceStore.getPagination.prev,
            }">
            <
          </button>

          <template v-for="n in getVisiblePages(page, maintenanceStore.getPagination.pages)" :key="n">
            <button
              v-if="typeof n === 'number'"
              @click="loadPage(n)"
              class="pagination-button"
              :class="{ 'active bg-blue-600 text-white': n === page, 'bg-gray-100 text-gray-700 hover:bg-gray-200': n !== page }">
              {{ n }}
            </button>
            <span v-else class="px-2 py-2 text-gray-500">{{ n }}</span>
          </template>

          <button
            :disabled="!maintenanceStore.getPagination.next"
            @click="loadPage(maintenanceStore.getPagination.next || maintenanceStore.getPagination.pages)"
            class="pagination-button"
            :class="{
              'cursor-not-allowed bg-gray-100 text-gray-400': !maintenanceStore.getPagination.next,
              'bg-gray-100 text-gray-700 hover:bg-gray-200': maintenanceStore.getPagination.next,
            }">
            >
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<style>
.pagination-button {
  padding: 0.5rem 1rem;
  transition: background-color 0.2s;
}

.pagination-button:disabled {
  background-color: #f3f4f6;
  color: #9ca3af;
  cursor: not-allowed;
}

.pagination-button.active {
  background-color: #2563eb;
  color: #ffffff;
}

#itemsPerPage {
  width: 65px;
  background-color: white;
}

#itemsPerPage option {
  background-color: #fff;
  color: #000;
}

dark #itemsPerPage option {
  background-color: #1f2937;
  color: #fff;
}
</style>
